import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import CustomModal from "../../../base-components/CustomModal";
import Popup from "../../../base-components/Popup";
import TablePaginationActions from "../../../base-components/TablePaginationActions";
import { getSessionUser } from "../../../utils/roleAccess";
import { PageHeader } from "../../../ui";
import {
  RoleScreensAndPermissions,
  toggleScreenWithRead,
} from "./RoleScreensAndPermissions";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function SuperAdminRoles() {
  const sessionUser = useMemo(() => getSessionUser(), []);
  const actingUserId = sessionUser?._id;

  const [roles, setRoles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const showPopup = (success, message) => {
    setIsSuccessAlert(success);
    setPopupMessage(message);
    setIsPopupOpen(true);
    setTimeout(() => setIsPopupOpen(false), 4000);
  };

  const loadRoles = () => {
    if (!actingUserId) return;
    fetch(`${BASE_URL}super-admin/roles?actingUserId=${actingUserId}`)
      .then((r) => r.json())
      .then((data) => setRoles(data.roles || []))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (!actingUserId) return;
    loadRoles();
    fetch(`${BASE_URL}super-admin/screens?actingUserId=${actingUserId}`)
      .then((r) => r.json())
      .then((data) => setScreens(data.screens || []))
      .catch((err) => console.error(err));
    fetch(`${BASE_URL}super-admin/permissions-catalog?actingUserId=${actingUserId}`)
      .then((r) => r.json())
      .then((data) => setPermissionCatalog(data.permissions || []))
      .catch((err) => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actingUserId]);

  const resetForm = () => {
    setRoleName("");
    setRoleDescription("");
    setSelectedScreens([]);
    setSelectedPermissions([]);
    setEditingRole(null);
  };

  const selectedPermissionsRef = useRef(selectedPermissions);
  selectedPermissionsRef.current = selectedPermissions;

  const toggleScreen = (key) => {
    setSelectedScreens((prevScreens) => {
      const { screens: nextScreens, permissions: nextPerms } = toggleScreenWithRead(
        prevScreens,
        selectedPermissionsRef.current,
        key,
        permissionCatalog
      );
      selectedPermissionsRef.current = nextPerms;
      setSelectedPermissions(nextPerms);
      return nextScreens;
    });
  };

  const togglePermission = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const openAdd = () => {
    resetForm();
    setIsAddModalOpen(true);
  };

  const openView = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || "");
    setSelectedScreens(Array.isArray(role.screens) ? role.screens : []);
    setSelectedPermissions(Array.isArray(role.permissions) ? role.permissions : []);
    setIsViewModalOpen(true);
  };

  const openUpdate = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || "");
    setSelectedScreens(Array.isArray(role.screens) ? [...role.screens] : []);
    setSelectedPermissions(Array.isArray(role.permissions) ? [...role.permissions] : []);
    setIsUpdateModalOpen(true);
  };

  const openDelete = (role) => {
    setRoleToDelete(role);
    setIsDeleteModalOpen(true);
  };

  const handleCreate = async () => {
    if (!actingUserId || !roleName.trim()) return;
    try {
      const res = await fetch(`${BASE_URL}super-admin/roles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actingUserId,
          name: roleName.trim(),
          description: roleDescription,
          screens: selectedScreens,
          permissions: selectedPermissions,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to create role");
      } else {
        showPopup(true, "Role created successfully");
        setIsAddModalOpen(false);
        resetForm();
        loadRoles();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to create role");
    }
  };

  const handleUpdate = async () => {
    if (!actingUserId || !editingRole) return;
    try {
      const body = {
        actingUserId,
        description: roleDescription,
        screens: selectedScreens,
        permissions: selectedPermissions,
      };
      if (!editingRole.is_system) {
        body.name = roleName.trim();
      }
      const res = await fetch(`${BASE_URL}super-admin/roles/${editingRole._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to update role");
      } else {
        showPopup(true, "Role updated successfully");
        setIsUpdateModalOpen(false);
        resetForm();
        loadRoles();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to update role");
    }
  };

  const handleDelete = async () => {
    if (!actingUserId || !roleToDelete) return;
    try {
      const res = await fetch(
        `${BASE_URL}super-admin/roles/${roleToDelete._id}?actingUserId=${actingUserId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ actingUserId }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        showPopup(false, data.message || "Failed to delete role");
      } else {
        showPopup(true, "Role deleted successfully");
        setIsDeleteModalOpen(false);
        setRoleToDelete(null);
        loadRoles();
      }
    } catch (err) {
      console.error(err);
      showPopup(false, "Failed to delete role");
    }
  };

  const ScreensAndPerms = ({ readOnly = false }) => (
    <RoleScreensAndPermissions
      screens={screens}
      permissionCatalog={permissionCatalog}
      selectedScreens={selectedScreens}
      selectedPermissions={selectedPermissions}
      onToggleScreen={toggleScreen}
      onTogglePermission={togglePermission}
      readOnly={readOnly}
    />
  );

  const paginated =
    rowsPerPage > 0
      ? roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : roles;

  if (!sessionUser || String(sessionUser.role).toLowerCase() !== "superadmin") {
    return (
      <div className="m-5 bg-gx-border p-5 rounded-lg">
        <h3 className="text-xl font-bold text-center text-gx-ink-soft">
          Super Admin access required
        </h3>
      </div>
    );
  }

  return (
    <>
      <div className="m-5">
        <PageHeader
          title="Role Management"
          actions={
                <button
                className="gx-cta"
                onClick={openAdd}
                >
                Add Role
                </button>
          }
        />
      </div>

      {roles.length > 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Screens</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((role) => (
                <TableRow hover key={role._id}>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{role.description || "—"}</TableCell>
                  <TableCell>
                    {role.is_system ? "System" : role.is_dynamic ? "Custom" : "—"}
                  </TableCell>
                  <TableCell>
                    {(role.screens || []).length} screen(s)
                  </TableCell>
                  <TableCell>
                    {(role.permissions || []).length} perm(s)
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 flex-wrap">
                      <div
                        className="text-gx-ink font-bold hover:cursor-pointer hover:text-green-700"
                        onClick={() => openView(role)}
                      >
                        <FontAwesomeIcon icon={faEye} /> View
                      </div>
                      <div
                        className="text-gx-ink font-bold hover:cursor-pointer hover:text-gx-red"
                        onClick={() => openUpdate(role)}
                      >
                        <FontAwesomeIcon icon={faPen} /> Edit Access
                      </div>
                      {!role.is_system && (
                        <div
                          className="text-gx-ink font-bold hover:cursor-pointer hover:text-red-700"
                          onClick={() => openDelete(role)}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  colSpan={6}
                  count={roles.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-10 rounded-lg m-10">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">
            No Roles Found
          </h3>
        </div>
      )}

      <CustomModal
        isModal={isAddModalOpen}
        handleModal={() => {
          setIsAddModalOpen(false);
          resetForm();
        }}
        size="4xl"
      >
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">
          Add Role
        </h1>
        <div className="m-5 text-gx-ink-soft">
          <label>Name</label>
          <input
            type="text"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="Enter role name"
          />
          <label>Description</label>
          <input
            type="text"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
            placeholder="Enter description"
          />
          <ScreensAndPerms />
          <div className="flex justify-end pt-3 mt-3 border-t">
            <button
              className="bg-blue-600 p-2 px-5 rounded-lg text-white font-medium hover:bg-blue-700"
              onClick={handleCreate}
            >
              Submit
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isModal={isUpdateModalOpen}
        handleModal={() => {
          setIsUpdateModalOpen(false);
          resetForm();
        }}
        size="4xl"
      >
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">
          {editingRole?.is_system ? "Edit Role Access" : "Update Role"}
        </h1>
        <div className="m-5 text-gx-ink-soft">
          <label>Name</label>
          <input
            type="text"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 mb-3"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            disabled={editingRole?.is_system}
          />
          <label>Description</label>
          <input
            type="text"
            className="border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0"
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
          />
          <ScreensAndPerms />
          <div className="flex justify-end pt-3 mt-3 border-t">
            <button
              className="bg-blue-600 p-2 px-5 rounded-lg text-white font-medium hover:bg-blue-700"
              onClick={handleUpdate}
            >
              Save
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isModal={isViewModalOpen}
        handleModal={() => {
          setIsViewModalOpen(false);
          resetForm();
        }}
        size="4xl"
      >
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">
          View Role
        </h1>
        <div className="m-5">
          <div className="flex gap-5 items-center">
            <h1 className="font-medium text-xl">Name:</h1>
            <span className="text-xl">{roleName}</span>
          </div>
          <div className="flex gap-5 items-center mt-2">
            <h1 className="font-medium text-xl">Description:</h1>
            <span className="text-xl">{roleDescription || "—"}</span>
          </div>
          <ScreensAndPerms readOnly />
        </div>
      </CustomModal>

      <CustomModal
        isModal={isDeleteModalOpen}
        handleModal={() => setIsDeleteModalOpen(false)}
        size="md"
      >
        <div className="m-5">
          <h2 className="text-xl font-medium">Delete Role?</h2>
          <p className="text-gx-ink-soft mt-3">
            Are you sure you want to delete role &quot;{roleToDelete?.name}&quot;?
          </p>
          <button
            className="bg-red-600 p-2 px-5 rounded-lg text-white font-medium hover:bg-red-700 mt-5 float-right mb-5"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </CustomModal>

      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? "success" : "failure"}
          message={popupMessage}
        />
      )}
    </>
  );
}

export default SuperAdminRoles;
